import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  GroupBlueIcon,
  ScheduleOrangeIcon,
  SmallScheduleIcon,
  ViewerGreyIcon,
} from "../../icons";
import { DeleteGroupDialog } from "./id/DeleteGroup";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutline";
import { TypographyNormal, TypographySmall } from "../customComponent";
import { colorStyling } from "../../helpers/color";
import { SET_DETAIL_GROUP } from "../../store/actionTypes/groupActionType";
import { parseVariables } from "../../helpers/localize";
import { red } from "@mui/material/colors";
import ViewerListDialog from "./ViewerListDialog";
import ScheduleListDialog from "./ScheduleListDialog";

const PREFIX = "GroupItem";

const classes = {
  hover: `${PREFIX}-hover`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const StyledBox = styled(TableRow)(() => ({
  [`& .${classes.hover}`]: {
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underline",
      color: colorStyling.primary,
      "& .text": {
        color: colorStyling.primary,
      },
    },
  },
}));

const GroupItem = ({ group, dispatch }) => {
  const navigate = useNavigate();

  const { t } = useTranslation("group");

  const [openViewerList, setOpenViewerList] = useState(false);
  const [openScheduleList, setOpenScheduleList] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorMenu, setAnchorMenu] = useState(null);
  const openMenu = Boolean(anchorMenu);

  const handleClickMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMenu(null);
  };

  const handleCloseViewerList = () => {
    setOpenViewerList(false);
  };

  const handleCloseScheduleList = () => {
    setOpenScheduleList(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const redirectToGroupDetailPage = (id) => {
    dispatch({ type: SET_DETAIL_GROUP, payload: group });
    navigate("/group/detail/" + id);
  };

  return (
    <StyledBox
      sx={{
        "&:hover": { backgroundColor: colorStyling.white.hover },
      }}
    >
      <TableCell align="left">
        <Box
          sx={{
            ml: 2,
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ViewerListDialog
            group={group}
            open={openViewerList}
            handleClose={handleCloseViewerList}
          />
          <ScheduleListDialog
            group={group}
            open={openScheduleList}
            handleClose={handleCloseScheduleList}
          />
          <DeleteGroupDialog
            group={group}
            open={openDeleteDialog}
            handleClose={handleCloseDeleteDialog}
            doNavigate={false}
          />

          <GroupBlueIcon />
          <Box ml={2}>
            <Tooltip
              title={group?.name?.length > 30 ? group.name : ""}
              placement="top-start"
            >
              <Box onClick={() => redirectToGroupDetailPage(group.id)}>
                <TypographyNormal
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      color: colorStyling.primary,
                    },
                  }}
                >
                  {group?.name?.length > 30
                    ? group.name.substring(0, 27).trim() + "..."
                    : group.name}
                </TypographyNormal>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </TableCell>

      <TableCell align="center">
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            className={classes.hover}
            onClick={(e) => {
              e.stopPropagation();
              setOpenViewerList(true);
            }}
          >
            <ViewerGreyIcon />
            {group.screensCount || group.screensCount === 0 ? (
              <TypographySmall
                className="text"
                sx={{
                  ml: "5px",
                  fontWeight: 500,
                  textDecoration: "underline",
                }}
              >
                {parseVariables(t("item.numViewers"), {
                  num: group.screensCount ?? 0,
                })}
              </TypographySmall>
            ) : (
              <Skeleton
                variant="text"
                sx={{
                  fontSize: "12px",
                  lineHeight: "24px",
                  width: "80px",
                }}
              />
            )}
          </Box>

          <SmallScheduleIcon />
        </Box>
      </TableCell>

      <TableCell align="center">
        <Box
          gap={3}
          sx={{
            pr: 1,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            className={classes.hover}
            onClick={(e) => {
              e.stopPropagation();
              setOpenScheduleList(true);
            }}
          >
            <ScheduleOrangeIcon />

            {group.schedulesCount || group.schedulesCount === 0 ? (
              <TypographySmall
                className="text"
                sx={{
                  ml: "5px",
                  fontWeight: 500,
                  textDecoration: "underline",
                }}
              >
                {parseVariables(t("item.numSchedules"), {
                  num: group.schedulesCount ?? 0,
                })}
              </TypographySmall>
            ) : (
              <Skeleton
                variant="text"
                sx={{
                  fontSize: "12px",
                  lineHeight: "24px",
                  width: "80px",
                }}
              />
            )}
          </Box>

          <Box>
            <IconButton
              sx={{ color: colorStyling.black }}
              onClick={(e) => {
                e.stopPropagation();
                handleClickMenu(e);
              }}
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              id="basic-menu"
              anchorEl={anchorMenu}
              open={openMenu}
              onClose={(e) => {
                e.stopPropagation();
                handleCloseMenu();
              }}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  redirectToGroupDetailPage(group.id);
                  handleCloseMenu();
                }}
                dense
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ marginRight: "5px" }}
                />
                {t("item.menuDetailsBtn")}
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDeleteDialog(true);
                  handleCloseMenu();
                }}
                dense
                sx={{ color: red[600] }}
              >
                <DeleteOutlinedIcon fontSize="small" sx={{ mr: "5px" }} />
                {t("item.menuDeleteBtn")}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </TableCell>
    </StyledBox>
  );
};

export default GroupItem;
